import { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosTransformer } from 'axios';
import { toast } from 'react-toastify';
import axios from 'axios';
import { getToken } from '../auth/UserService';
import i18n from 'i18next';

const localBuildingDirectoryBaseUrl = 'http://localhost:7071/api';

/**
 * Production builds: use a relative base path so webpack never falls back to REACT_APP_BUILDING_DIRECTORY_API_URL
 * (that env is undefined at bundle build time when window is missing, which used to bake in Azure URLs).
 * Server must set BuildingDirectory:ProxyTargetBaseUrl. Optional: DisableSecurity on the Function App for MSAL bypass.
 * Opt out to direct Azure: REACT_APP_BUILDING_DIRECTORY_USE_DIRECT_AZURE=true + REACT_APP_BUILDING_DIRECTORY_API_URL.
 * nginx: REACT_APP_BUILDING_DIRECTORY_SAME_ORIGIN=true → /api/building-directory
 */
function resolveBuildingDirectoryBaseUrl(): string {
    if (process.env.NODE_ENV === 'development') {
        return process.env.REACT_APP_BUILDING_DIRECTORY_LOCAL_API_URL || localBuildingDirectoryBaseUrl;
    }

    if (
        process.env.REACT_APP_BUILDING_DIRECTORY_USE_DIRECT_AZURE === 'true' &&
        process.env.REACT_APP_BUILDING_DIRECTORY_API_URL
    ) {
        return process.env.REACT_APP_BUILDING_DIRECTORY_API_URL as string;
    }

    if (process.env.REACT_APP_BUILDING_DIRECTORY_SAME_ORIGIN === 'true') {
        return '/api/building-directory';
    }

    return '/api/building-directory-proxy';
}

// Resolve base URL on each request: at webpack build time `window` is undefined, so a one-time
// resolve incorrectly bakes in REACT_APP_BUILDING_DIRECTORY_API_URL (Azure). Browser must use /api/building-directory-proxy.
const buildingDirectory = axios.create({
    baseURL: '',
    headers: {
        common: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
    },
});

const onRequest = (config: AxiosRequestConfig): AxiosRequestConfig => {
    config.baseURL = resolveBuildingDirectoryBaseUrl();

    const token = getToken();

    if (token && token !== '') {
        config.headers.common = {
            ...config.headers.common,
            Authorization: `Bearer ${token}`,
        };
    }

    return config;
};

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
    console.error(`[response error] [${JSON.stringify(error)}]`);

    return Promise.reject(error);
};

const onResponse = (response: AxiosResponse): AxiosResponse => {
    // Do something with response data
    if (response.status === 201) {
        // Do something
    }

    if (response.status === 202) {
        // Do something
    }

    return response;
};

const onResponseError = (error: AxiosError): Promise<AxiosError> => {
    const isNetworkError = !error.response;
    const status = error.response?.status;

    if (isNetworkError || status === 502 || status === 503 || status === 504) {
        console.warn('[buildingDirectory] API unavailable. Using fallback data when supported.');
        return Promise.reject(error);
    }

    if (status === 400 && error.response) {
        toast.error(i18n.t(`b:${error.response.data.errorMessage}`), { className: 'error-toast' });
    }

    if (status === 500) {
        // Do something
    }

    if (!axios.isCancel(error)) {
        toast.error(`[response error] [${JSON.stringify(error)}]`, { className: 'error-toast' });
    }

    return Promise.reject(error);
};

export const defaultTransformers = (): AxiosTransformer[] => {
    const { transformRequest } = axios.defaults;
    if (!transformRequest) {
        return [];
    } else if (transformRequest instanceof Array) {
        return transformRequest;
    } else {
        return [transformRequest];
    }
};

buildingDirectory.interceptors.response.use(onResponse, onResponseError);
buildingDirectory.interceptors.request.use(onRequest, onRequestError);

export default buildingDirectory;
